/**
 * Pure omnisearch-string utilities shared across V1 and V2 omnisearch hooks.
 *
 * Previously these four functions were copy-pasted across 8 files. See
 * apps/oak/src/hooks/omnisearch-v2/REVIEW.md (item #1) for the audit trail.
 */

import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";

/** Tokenize an omnisearch string, respecting `"…"` and `'…'` quoted groups. */
export function parseOmnisearchTokens(search: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let inQuotes = false;
  let quoteChar = "";

  for (let i = 0; i < search.length; i++) {
    const char = search[i];

    if (!inQuotes && (char === '"' || char === "'")) {
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      inQuotes = false;
      quoteChar = "";
      current += char;
    } else if (!inQuotes && char === " ") {
      if (current.trim()) {
        tokens.push(current.trim());
        current = "";
      }
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    tokens.push(current.trim());
  }

  return tokens;
}

/** Split an omnisearch string into KVP tokens (`key:value`) and non-KVP tokens. */
export function breakApartOmnisearch(search: string): {
  nonKvpValues: string;
  kvpValues: [string, string][];
} {
  const tokens = parseOmnisearchTokens(search);
  const nonKvpValues: string[] = [];
  const kvpValues: [string, string][] = [];

  for (const token of tokens) {
    const colonIndex = token.indexOf(":");
    if (colonIndex > 0) {
      const key = token.substring(0, colonIndex);
      const value = token.substring(colonIndex + 1);
      kvpValues.push([key, value]);
    } else {
      nonKvpValues.push(token);
    }
  }

  return {
    nonKvpValues: nonKvpValues.join(" "),
    kvpValues,
  };
}

/** Upsert a `key:value` pair into the omnisearch string. */
export function addOrUpdateKeyInOmnisearch(search: string, key: string, value: string): string {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  const baseOmnisearch = `${nonKvpValues} ${filteredKvp
    .map(([k, v]) => `${k}:${v}`)
    .join(" ")}`.trim();
  const newKeyValue = `${key}:${value}`;
  return baseOmnisearch ? `${baseOmnisearch} ${newKeyValue}` : newKeyValue;
}

/** Remove every `key:value` pair matching `key` from the omnisearch string. */
export function removeKeyInOmnisearch(search: string, key: string): string {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  return `${nonKvpValues} ${filteredKvp.map(([k, v]) => `${k}:${v}`).join(" ")}`.trim();
}

/** Quote a filter value when it would break tokenization, matching FilterPopover. */
export function quoteOmnisearchValue(value: string): string {
  return value.includes(" ") || value.includes(",") || value.includes("|") ? `"${value}"` : value;
}

/**
 * A `key:value` pair owned by a dedicated toolbar control (e.g. a checkbox),
 * hidden from the omnisearch input's *display* while it remains part
 * of the real omnisearch string. Only a single-value token carrying exactly
 * this (unquoted) value is hidden — a token with the same key but another value
 * stays visible, since no toolbar control reflects it. The control's
 * active-state check must use {@link hasOmnisearchToken} so hiding and
 * highlighting agree on what matches.
 */
export type HiddenDisplayToken = {
  key: string;
  value: string;
};

/**
 * True when a parsed `key -> values` pair is exactly a control-owned token.
 * The single predicate behind text-input stripping AND pill-row hiding — the
 * two displays must agree on what a control owns. An empty expected value
 * never matches: a caller without a user email builds { key, value: "" }, and
 * matching it would hide/rewrite `key:""` tokens.
 */
export function matchesHiddenDisplayToken(
  key: string,
  values: readonly string[],
  hidden: HiddenDisplayToken[]
): boolean {
  return hidden.some(
    (h) => h.value !== "" && h.key === key && values.length === 1 && values[0] === h.value
  );
}

function matchedHiddenPairs(search: string, hidden: HiddenDisplayToken[]) {
  return extractKeyValuePairs(search).filter((pair) =>
    matchesHiddenDisplayToken(pair.key, pair.value, hidden)
  );
}

/**
 * True when the omnisearch contains exactly `key:value` as a single-value
 * token. The one predicate behind both a toolbar toggle's active state and
 * hidden-token display stripping — the two must never disagree, or a filter
 * ends up applied with no visible indicator (or indicated twice).
 */
export function hasOmnisearchToken(search: string, token: HiddenDisplayToken): boolean {
  return matchedHiddenPairs(search, [token]).length > 0;
}

/**
 * The display value for the search input: the omnisearch minus hidden tokens,
 * sliced out of the original string so the surviving text keeps its order.
 * Only each removal seam is tidied (plus any orphaned `,`/`|` the cut left
 * behind) — surviving text, including whitespace inside quoted values, stays
 * byte-identical.
 */
export function stripHiddenDisplayTokens(search: string, hidden: HiddenDisplayToken[]): string {
  if (hidden.length === 0 || !search) return search;
  const matched = matchedHiddenPairs(search, hidden);
  if (matched.length === 0) return search;
  let result = search;
  // Back to front so earlier spans' indices stay valid; trimming only touches
  // text between the previous token's end and this cut, never earlier spans.
  for (const pair of [...matched].reverse()) {
    const before = result.slice(0, pair.start).trimEnd();
    const after = result.slice(pair.end).replace(/^[\s,|]+/, "");
    result = before && after ? `${before} ${after}` : `${before}${after}`;
  }
  return result;
}

/**
 * Rebuild the real omnisearch from a user-edited display value.
 *
 * Preserves every token in `fullOmnisearch` the user could not see while
 * editing: hidden control-owned tokens, plus any filter another control (a
 * chart segment, a toolbar checkbox) wrote after the edit began —
 * `baseOmnisearch` is the omnisearch as of the first keystroke; it defaults to
 * `fullOmnisearch` for callers with no edit window. A key the user typed
 * themselves always wins, and a visible token the user deleted stays deleted.
 *
 * Placement is verified, not assumed: mid-edit text can end in `,`, `|`, or a
 * dangling `key:` (which swallow a token placed AFTER it) or start with a
 * separator (which swallows a token placed BEFORE it). Prefer prepending, fall
 * back to appending; when NEITHER placement keeps every preserved token (and
 * the typed pairs) parseable, returns null — the caller must skip the write
 * rather than commit a corrupted query.
 */
export function mergeHiddenDisplayTokens(
  displayValue: string,
  fullOmnisearch: string,
  hidden: HiddenDisplayToken[],
  baseOmnisearch: string = fullOmnisearch
): string | null {
  const visibleKeysAtEditStart = new Set(
    extractKeyValuePairs(stripHiddenDisplayTokens(baseOmnisearch, hidden)).map((pair) => pair.key)
  );
  const typedPairs = extractKeyValuePairs(displayValue);
  const typedKeys = new Set(typedPairs.map((pair) => pair.key));
  const preserved = extractKeyValuePairs(fullOmnisearch).filter(
    (pair) => !visibleKeysAtEditStart.has(pair.key) && !typedKeys.has(pair.key)
  );
  if (preserved.length === 0) return displayValue;

  // Byte-exact token text from the source string, so quoting and multi-value
  // separators survive the round trip.
  const tokens = preserved.map((pair) => fullOmnisearch.slice(pair.start, pair.end));
  const join = (parts: string[]) => parts.filter((part) => part !== "").join(" ");
  const survivesIntact = (candidate: string) => {
    const pairs = extractKeyValuePairs(candidate);
    if (pairs.length !== typedPairs.length + preserved.length) return false;
    return preserved.every((expected) =>
      pairs.some(
        (pair) =>
          pair.key === expected.key &&
          pair.value.length === expected.value.length &&
          pair.value.every((v, i) => v === expected.value[i])
      )
    );
  };

  const prepended = join([...tokens, displayValue]);
  if (survivesIntact(prepended)) return prepended;
  const appended = join([displayValue, ...tokens]);
  if (survivesIntact(appended)) return appended;
  return null;
}
